import React, { type FC, useMemo, useState } from 'react';

import {
  Biomarkers,
  DocumentList,
  DocumentPagination,
  GeneralInfo,
  Media,
  Page,
} from 'components';
import { useSharedStorageDetails } from 'hooks';

import { Parse } from 'utils';
import Grid from '@mui/material/Grid';
import { MediaModal } from '../Media/fragments';
import { BlockTypes, LayoutTypes } from './types';
import { stylesByCondition } from './styles';
import { LAYOUT_CONFIG } from './constants';
import { useCurrentBreakpoint } from '../../hooks/useCurrentBreakpoint';

export const Main: FC = () => {
  const { id } = Parse.getAppPathnames();
  const { data, isFetching, isError } = useSharedStorageDetails({ id });
  const [selectedFileIndex, setSelectedFileIndex] = useState<number>(0);
  const [isFileDialogOpen, setIsFileDialogOpen] = useState(false);
  const selectedFile = data?.[selectedFileIndex];

  const isMoreOneDocument = data ? data.length > 1 : false;

  const handleFileDialogOpen = () => {
    setIsFileDialogOpen(true);
  };
  const handleFileDialogClose = () => {
    setIsFileDialogOpen(false);
  };

  const layout = useMemo(() => {
    let styleCode: string = LayoutTypes.DI;
    if (data) {
      if (data.length > 1) {
        styleCode = 'DL_D';
      } else {
        styleCode = 'D';
      }
      if (selectedFile) {
        if (selectedFile.biomarkers?.length) {
          styleCode += 'B';
        }
        if (selectedFile.size) {
          styleCode += 'I';
        }
      }
    }

    return styleCode as LayoutTypes;
  }, [data, selectedFile]);

  const breakpoint = useCurrentBreakpoint();
  const config = LAYOUT_CONFIG[layout];

  const currentConfig = config.breakpoints[breakpoint];

  const columns: Record<number, string[]> = {};
  config.blocks.forEach((block, index) => {
    const col = currentConfig[index] || 0;
    if (!columns[col]) columns[col] = [];
    columns[col].push(block);
  });

  const columnCount = Math.max(...Object.keys(columns).map(Number));

  return (
    <Page isError={isError} isLoading={isFetching}>
      {data && (
        <>
          <Grid container sx={stylesByCondition[layout]}>
            {Array.from({ length: columnCount }, (_, i) => i + 1).map(
              (colNum) => (
                <Grid item key={colNum}>
                  {columns[colNum]?.map((block) => (
                    <React.Fragment key={block}>
                      {block === BlockTypes.DOCUMENTS_LIST &&
                        breakpoint !== 'xs' && (
                          <DocumentList
                            files={data}
                            selectedFileIndex={selectedFileIndex}
                            onSelect={setSelectedFileIndex}
                          />
                        )}
                      {block === BlockTypes.DESCRIPTION && selectedFile && (
                        <GeneralInfo
                          id={id}
                          data={selectedFile}
                          onClick={handleFileDialogOpen}
                        />
                      )}
                      {block === BlockTypes.BIOMARKERS &&
                        selectedFile?.biomarkers && (
                          <Biomarkers
                            data={selectedFile.biomarkers}
                            isMoreOneDocument={isMoreOneDocument}
                          />
                        )}
                      {block === BlockTypes.IMAGE && selectedFile && (
                        <Media
                          id={id}
                          fileId={selectedFile.id}
                          onClick={handleFileDialogOpen}
                        />
                      )}
                    </React.Fragment>
                  ))}
                </Grid>
              )
            )}
          </Grid>
          {breakpoint === 'xs' && isMoreOneDocument && (
            <DocumentPagination
              files={data}
              selectedFileIndex={selectedFileIndex}
              onSelect={setSelectedFileIndex}
            />
          )}
          {selectedFile && (
            <MediaModal
              isOpen={isFileDialogOpen}
              onClose={handleFileDialogClose}
              id={id}
              fileId={selectedFile.id}
            />
          )}
        </>
      )}
    </Page>
  );
};
